import { observer } from 'mobx-react-lite';
import { Modal, ModalProps } from '../modal';
import { useTranslationsContext } from 'store/translations';
import { Button } from 'components/core';

import Wheel from 'assets/img/wheel_card.png';

import './rewardsModal.styl';
import { useStoreContext } from 'store/app';
import { IRewardState, LotteryRewardTypes, QuestTypes } from 'src/types';
import { useNavigate } from 'react-router';
import { routes } from 'utils/router';
import { useEffect, useState } from 'react';
import { RewardDetailsModal } from './rewardDetailsModal';
import { get14DaysModalData } from 'utils/get14DaysModalData';
import { getRubiesCardData } from 'utils/getRubiesCardData';
import { RewardCard } from './rewardCard';

interface RewardsModalProps extends ModalProps {}

const imageRewardTypes = [
    LotteryRewardTypes.FRAME,
    LotteryRewardTypes.GIFT,
    LotteryRewardTypes.SURPRIZE,
    LotteryRewardTypes.CODE,
    LotteryRewardTypes.LOGINS_SPECIAL,
    LotteryRewardTypes.RUBIES_SPECIAL,
    LotteryRewardTypes.OKS
];

export const RewardsModal = observer(({ preopenId, onPreopenOpened, ...rest }: RewardsModalProps) => {
    const { translate } = useTranslationsContext();
    const { questsConfig, saveRewardsSnapshot, getVisitedDaysAmount, logOperation, revolverPrize, setRevolverPrize } =
        useStoreContext();
    const navigate = useNavigate();
    const [selectedReward, setSelectedReward] = useState<IRewardState>();

    const { rewards } = questsConfig || {};

    const rewardsSorter = (reward1: IRewardState, reward2: IRewardState) => {
        if (reward1.rolled === false) {
            return -1;
        }

        if (reward2.rolled === false) {
            return 1;
        }

        return -1;
    };

    const rewardsPrepared = (rewards || []).slice().sort(rewardsSorter);

    let lastRevolverIndex = -1;
    for (let i = 0; i < rewardsPrepared.length; i++) {
        if (rewardsPrepared[i].rolled) {
            break;
        }

        lastRevolverIndex = i;
    }

    useEffect(() => {
        logOperation('rewards', 'render');
        saveRewardsSnapshot();

        if (preopenId || revolverPrize) {
            let preopenReward: IRewardState | undefined;
            if (revolverPrize) {
                preopenReward = revolverPrize;
                if (!preopenReward?.renderParams.modalTitle) {
                    preopenReward = undefined;
                }

                setRevolverPrize(null);
            } else {
                preopenReward = questsConfig?.rewards?.find((x) => x.id === preopenId!);
            }

            if (preopenReward) {
                setSelectedReward(preopenReward);
            }

            onPreopenOpened?.();
        }
    }, []);

    const renderUnopenedReward = (reward: IRewardState, index: number) => {
        const relatedQuest = reward.mission_id
            ? questsConfig?.tasks.find((x) => x.key === reward.mission_id)
            : undefined;

        return (
            <div key={index} className="rewards-modal-card unopened">
                <div className="rewards-modal-card-content">
                    <div className="rewards-modal-card-texts">
                        <div className="rewards-modal-card-title">{translate('rewards.unopened.title')}</div>
                        <div className="rewards-modal-card-description">
                            {relatedQuest?.title
                                ? translate('rewards.unopened.description', { name: relatedQuest.title })
                                : translate('rewards.unopened.description.unnamed')}
                        </div>
                    </div>

                    <img className="rewards-modal-card-image" src={Wheel} />
                </div>
                <Button
                    onClick={() => navigate(routes.wheel, { state: { missionId: reward.mission_id } })}
                    className="rewards-modal-card-button"
                >
                    {translate('rewards.unopened.button')}
                </Button>
            </div>
        );
    };

    const renderReward = (reward: IRewardState, index: number) => {
        return <RewardCard key={index} reward={reward} setSelectedReward={setSelectedReward} />;
    };

    const cardByTypeRenderer = (reward: IRewardState, index: number) => {
        if (reward.rolled === false) {
            return renderUnopenedReward(reward, index);
        }

        if (imageRewardTypes.includes(reward.lottery_reward_type)) {
            return renderReward(reward, index);
        }

        return null;
    };

    const quest14days = questsConfig?.tasks.find((x) => x.newYearType === QuestTypes.LOGINS);

    if (quest14days && quest14days.done != 1) {
        const daysLeft = quest14days.goal! - getVisitedDaysAmount();
        rewardsPrepared.splice(
            lastRevolverIndex + 1,
            0,
            get14DaysModalData(translate, quest14days, daysLeft, quest14days.goal || 14)
        );
    }

    const rubiesAmount = rewardsPrepared.reduce((acc, x) => {
        if (x.rolled && x.lottery_reward_type === LotteryRewardTypes.RUBY_100) {
            return acc + 100;
        }

        if (x.rolled && x.lottery_reward_type === LotteryRewardTypes.RUBY_50) {
            return acc + 50;
        }

        return acc;
    }, 0);

    if (rubiesAmount) {
        rewardsPrepared.splice(lastRevolverIndex + 1, 0, getRubiesCardData(translate, rubiesAmount));
    }

    return (
        <Modal title={translate('rewards.title')} className="rewards-modal-wrapper" {...rest}>
            <div className="rewards-modal">{rewardsPrepared.map(cardByTypeRenderer)}</div>
            <RewardDetailsModal
                reward={selectedReward!}
                isOpened={!!selectedReward}
                onClose={() => setSelectedReward(undefined)}
            />
        </Modal>
    );
});
